import { IMPORT_SLOT_ID } from './ProductImport';
import { MaterialsPanel } from './MaterialsPanel';
import { GENERATE_SLOT_ID } from './MeshGenerateControls';
import { appliedIdOf, useMaterialsRun } from './materialsRun';
import { useEffect } from 'react';
import { FindHolesNotes } from './FindHoles';
import { JointsRun, useJointRequests, useJointsRunning } from './JointsRun';
import { PhaseControls } from './PhaseControls';
import { PartsPhase } from './PartsPhase';
import { JointHoles, JointRunScene } from './JointHoles';
import { SingleModels } from './SingleModels';
import { ArrowLeft } from 'lucide-react';
import { SectionNav } from './SectionNav';
import { SubphaseId } from './phaseBusy';
import { HardwareAxisBadge } from './HardwareAxisTable';
import { DrillingTable } from './DrillingTable';
import { useHardwareTab, type HardwareTab } from './hardwareTab';
import { useHardwareInteractions } from './hardwareInteractionsRun';
import { usePlace } from './lastPlace';
import { HardwareTableBadge } from './HardwareTable';
import { useGeneratingSubs, useModelsStages } from './modelsStages';
import { useProductLabel } from './ProductColumn';
import { useSubmodelFilter } from './submodelFilterStore';
import { ToolFitTable } from './ToolFitTable';
import { useLanguage } from '../../contexts/LanguageContext';
import { modelsSub } from './appScreens';

// WHAT THE PRODUCT IS MADE OF, AND THE MESHES MADE OF IT — two phases, in the order they depend
// on each other.
//
// TWO QUESTIONS THAT WERE ONE CALL. The written engine used to answer both at once: its
// prompt asked for MATERIAL lines and then for the bpy, so a colour was a property of
// whichever run happened to be pressed. Two builds of one chair came out two browns, a
// re-run asked for a shape correction re-judged the fabric on the way past, and the
// measured texture could only be found by walking that product's runs looking for the
// newest one that had any.
//
// None of that is about 3D. What a chair is made of is a fact about the chair — so it is
// read once, kept per submodel, and handed to every build (see materialsRun and
// generate_written, which takes `materials` in the request). The models phase is the mesh
// phase as it was, with that half of its prompt removed.
//
// They were one phase with a switcher of two cards; they are two phases now, each with its
// own heading and tooltip from the pipeline (see viewerPipelines).

// THE MATERIALS PHASE: the product's own, read per colourway off its photographs — and the
// ones no photograph shows, the hardware's, edited by hand (see standardMaterials) — and which
// part is made of which. One table holds all three (see MaterialsTable): they are what every
// mesh of the next phase is painted with, so they are answered in one place.
export function MaterialsPhase({ docId, file, locked }: { docId: string; file: Blob; locked?: boolean }) {
  const submodel = useSubmodelFilter(docId);
  // WHAT THE PRODUCT IS CALLED — it goes into the materials question, and "model this dining
  // chair" is a better question than "model this".
  const label = useProductLabel(docId);

  return (
    <div className="flex flex-col gap-6">
      {/* Read per colourway — the one chosen for the document (see SubmodelPicker). */}
      <MaterialsPanel docId={docId} submodel={submodel} label={label} file={file} locked={locked} />
    </div>
  );
}

// THE MODELS PHASE: the meshes, built with the materials the phase before applied — in four
// subphases, in the order the work depends on itself (the tools last, which nothing waits on). The whole product first: every component
// part's mesh is cut out of it and out of nothing else. The hardware, from the manual's own
// drawings — after the hardware table, its first half: which pieces of hardware work together,
// one call over the sub-steps, which the hardware is built from (a set is built from its row, and
// the table says which pieces are covers). Then the component parts, which arrive ready-made from the product, for the holes
// the manual gives them — after the hardware, because a bore's width and the fit shown in it
// are read off the hardware's own model — and with them the covers, which go in those holes and
// under those parts' feet, placed joint by joint where the holes are checked.
export function ModelsPhase({ docId, file, locked, held, folder = false }: {
  docId: string;
  file: Blob;
  locked?: boolean;
  held?: boolean;
  // A FOLDER, no manual and so no materials phase: nothing here waits on a reading it cannot have.
  folder?: boolean;
}) {
  const { t } = useLanguage();
  const submodel = useSubmodelFilter(docId);
  // WHETHER THIS COLOURWAY HAS A READING ON ITS MESHES — the gate on every subphase that builds
  // or reads one. Not on the hardware interactions: a reading of the sub-steps, no mesh in it.
  const applied = !!appliedIdOf(useMaterialsRun(docId), submodel);
  // Where this browser was inside the phase. Not what the notice knows: where you are looking.
  const [saved, setSub] = usePlace(docId, 'modelsSub');
  const sub = modelsSub(saved);
  const stages = useModelsStages(docId);
  // THE COMPONENT PARTS' HOLES, BY JOINT OR BY PART: a Find holes group as one thing — its parts
  // together and what it found in them — or one part, where its holes are drilled. By joint
  // first: it is the question each call answered.
  const [holesView, setHolesView] = usePlace(docId, 'holesView');
  const byJoint = holesView !== 'part';
  // A RUN ASKED FOR WITH A PART OPEN: it is made in the joints' view, so back to it.
  const coverRequests = useJointRequests(docId);
  // DRILLING HOLES AT WORK: the run is on, or still to reach, a hardware joint.
  const drilling = useJointsRunning(docId, 'holes');
  // FIND HARDWARE INTERACTIONS AT WORK, which Hardware & covers' card says whichever is open.
  const hardwareTable = useHardwareInteractions(docId);
  // A PART'S MODEL BEING BUILT, whichever part of the subphase is open.
  const generating = useGeneratingSubs(docId);
  useEffect(() => {
    if (coverRequests.queue.length && !byJoint) setHolesView('joint');
  }, [coverRequests.queue.length, byJoint, setHolesView]);

  const nav = (
    <SectionNav
      items={[
        { id: 'product', label: t('notice.models.subProduct'), fraction: stages.product.fraction },
        { id: 'tools', label: t('notice.models.subTools'), fraction: stages.tools.fraction,
          busy: generating.has('tools') },
        { id: 'hardware', label: t('notice.models.subHardware'), fraction: stages.hardware.fraction,
          busy: hardwareTable.busy || generating.has('hardware') },
        { id: 'components', label: t('notice.models.subComponents'),
          fraction: stages.components.fraction, busy: drilling || generating.has('components') },
        { id: 'single', label: t('notice.models.subSingle'), fraction: stages.single.fraction },
      ]}
      active={sub}
      onSelect={setSub}
    />
  );

  // HARDWARE & COVERS: THE TABLE FIRST, whatever the materials — which pieces of hardware work
  // together, what the models below are built from — its badge beside the models' own, the table
  // over the strip. It was a subphase of its own; they work together.
  const table = sub === 'hardware' && <HardwareTableBadge docId={docId} file={file} locked={locked} />;
  // HARDWARE & COVERS' OPEN TAB, and a badge that is not about it greyed and shut.
  const hwTab = useHardwareTab(docId);
  const off = (tab: HardwareTab) => (sub === 'hardware' && hwTab !== tab
    ? { className: 'contents [&>*]:opacity-40', inert: true }
    : { className: 'contents' });

  // THE MODELS ARE DRAWN WHATEVER THE COLOURWAY HAS APPLIED. A model is the product's shape, the
  // same in every colourway (see _copy_to_colourways), and one copied into a colourway with no
  // reading yet wears grey — no colour rather than another colourway's. What waits for a reading
  // is BUILDING one: a build is painted with the materials it is given, and there are none.
  // AND ONLY WHERE A COLOURWAY IS IN IT: the product and its components. Hardware and tools are
  // the same objects in every colourway, painted with the standard materials, which need no
  // reading — their subphases are neither greyed nor locked for want of one.
  const waiting = !folder && !applied && (sub === 'product' || sub === 'components');
  return (
      <div className="flex flex-col gap-6">
        {/* THE CALLS OF THE OPEN SUBPHASE, over the selector — each where it acts: generation
            on what is open, Holes & covers on the component parts' joints. */}
        {/* WHAT IS DRAWN FOR THE OPEN SUBPHASE, its card hatched while its badges spin. */}
        <SubphaseId.Provider value={sub}>
        <PhaseControls>
          {/* THE HARDWARE TABLE FIRST: the models are built from it. */}
          {/* EACH GREYED AND SHUT WHERE THE OPEN TAB IS NOT ITS OWN (see hardwareTab): still there,
              not pressable — `inert` takes the whole subtree out of the pointer and the keyboard.
              Dimmed on its children: a `contents` box draws nothing to dim. */}
          <span {...off('table')}>{table}</span>
          {/* 3D MODEL GENERATION, for whatever the subphase has open — drawn here by that
              generator (see MeshGenerateControls). Empty where nothing is generated. */}
          <span id={GENERATE_SLOT_ID} {...off('models')} />
          {/* AND THE PRODUCT BROUGHT IN FROM A FILE, beside it — see ProductImport. */}
          <span id={IMPORT_SLOT_ID} {...off('models')} />
          {/* AND HOW THE HARDWARE GOES IN, last: it is asked with the dimensions of the models the
              badge before it makes — see HardwareAxisTable. */}
          {sub === 'hardware' && <span {...off('axis')}><HardwareAxisBadge docId={docId} locked={locked} /></span>}
          {sub === 'components' && <JointsRun docId={docId} file={file} locked={locked} kind="holes" />}
        </PhaseControls>
        {nav}
        {waiting && (
          <p className="mx-auto max-w-xl text-center text-sm text-amber-300">{t('notice.3d.noApplied')}</p>
        )}
        {/* WHAT THE LAST HOLE SEARCH COULD NOT PLACE, over the holes it is about. */}
        {sub === 'components' && <FindHolesNotes docId={docId} />}
        {/* A PART OPENED FROM A JOINT, to be drilled: its own view, and the way back. */}
        {sub === 'components' && !byJoint && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setHolesView('joint')}
              className="inline-flex items-center gap-1 text-sm text-blue-600 underline-offset-2 hover:underline"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('notice.joints.back')}
            </button>
          </div>
        )}
        {/* THE TOOLS' TABLE, over their strip: which tool turns which hardware, and whether it fits. */}
        {sub === 'tools' && <ToolFitTable docId={docId} />}
        {/* THE RUN'S OWN SCENE, whichever subphase is open (see JointRunScene). */}
        <JointRunScene docId={docId} />
        {sub === 'single' ? <SingleModels docId={docId} /> : sub === 'components' && byJoint
          ? (
            <JointHoles
              docId={docId}
              kind="holes"
              onOpenPart={() => setHolesView('part')}
              // WHAT TO DRILL, hole by hole — from the Joints and Insertion tables — the first tab.
              table={{ label: t('notice.drilling.title'), body: <DrillingTable docId={docId} /> }}
            />
          )
          : <PartsPhase file={file} docId={docId} locked={locked || waiting} held={held} models view="meshes" sub={sub} />}
        </SubphaseId.Provider>
      </div>
    );
}
